<template>
  <div>
    <v-container fluid class="">
      <v-card>
        <v-card-title>
          Daily Schedules
          
          <v-toolbar flat>
            <v-btn fab text small color="grey darken-2" @click="prev">
              <v-icon small>
                mdi-chevron-left
              </v-icon>
            </v-btn>
            <v-btn fab text small color="grey darken-2" @click="next">
              <v-icon small>
                mdi-chevron-right
              </v-icon>
            </v-btn>
            <v-toolbar-title>
              <small>{{ this.momentLocal(this.date).format("MMM DD YYYY") }}</small>
            </v-toolbar-title>
            <v-btn outlined class="ml-4" color="grey darken-2" @click="today">
              Today
            </v-btn>
          </v-toolbar>
        </v-card-title>
        <template>
          <v-card>
            <v-skeleton-loader
              v-if="schedLoading || loading"
              class="mx-auto"
              type="table"
            ></v-skeleton-loader>
            <v-data-table
              v-else
              :headers="headers"
              :items="formattedItems"
              class="elevation-1"
              :options.sync="tableOptions"
              :server-items-length="totalItems"
              :loading="schedLoading || loading"
              :footer-props="{
                'items-per-page-options': [10, 20, 30, 40, 50, 100, 200],
              }"
              :disable-pagination="true"
            >

              <template v-slot:item.taskTime="{ item }">
                <span style="white-space:nowrap">
                  <v-chip
                    small
                    :color="item.color || item.taskColor || '#a59b9b'"
                    class="mr-1"
                  />
                  <span v-if="item.isAllDay">All Day</span>
                  <span v-else>
                    <span
                      v-if="item.fStartDate"
                      style="color: #424242"
                    >
                      {{ item.fStartDate }} &nbsp;
                    </span>
                    {{ item.fStartTime }} –&nbsp;
                    <span
                      v-if="item.fEndDate"
                      style="color: #424242"
                    >
                    {{ item.fEndDate }} &nbsp;
                    </span>
                    {{ item.fEndTime }}
                  </span>
                </span>
              </template>

              <template v-slot:item.description="{ item }">
                <DescriptionDialog :text="item.description" />
              </template>

              <template v-slot:item.employees="{ item }">
                <div class="my-1">
                  <v-chip
                    small
                    v-for="employee in item.employees"
                    :key="employee.id"
                    class="my-1 mr-1"
                    color="teal"
                    style="color: white"
                  >
                    {{ employee.firstName + " " + employee.lastName }}
                  </v-chip>
                </div>
              </template>

              <template v-slot:item.taskName="{ item }">
                <div v-if="$vuetify.breakpoint.xsOnly">
                  <span @click="onEdit(item)">{{ item.taskName }}</span>
                </div>
                <div
                  v-else
                  :style="nameLen">
                  <span
                    v-if="item.taskName.length <= 175"
                    @click="onEdit(item)"
                  >{{ item.taskName }}</span>
                  <v-tooltip v-else>
                    <template v-slot:activator="{ on, attrs }">
                      <span
                        @click="onEdit(item)"
                        v-bind="attrs"
                        v-on="on"
                      >
                        {{ item.taskName.slice(0,175) + "..." }}
                      </span>
                    </template>
                  </v-tooltip>
                </div>
              </template>

              <template v-slot:item.client="{ item }">
                <ClientChip
                  :clientId="item.clientId"
                  :clientName="item.clientCompanyName"
                />
              </template>
            </v-data-table>
          </v-card>
        </template>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from "vuex";
import formatterMixin from "@/mixins/formatterMixin";
import ClientChip from "components/ClientChip";
import DescriptionDialog from "components/DescriptionDialog";
import _ from "lodash";


export default {
  mixins: [formatterMixin],
  components: {
    ClientChip,
    DescriptionDialog,
  },
  data: () => ({
    date: "",
    showForm: false,
    selectedItem: {},
    timer: null,
    tableOptions: {},
    formattedItems: [],
    initialPageLoad: true,
    loading: true,
    headers: [
      { text: "Time", value: "taskTime", align: "start", sortable: false },
      { text: "Assigned To", value: "employees", align: "start", sortable: false },
      { text: "Client", value: "client", align: "start", sortable: false },
      { text: "Job Code", value: "taskCode", align: "start", sortable: false },
      { text: "Job Name", value: "taskName", align: "start", sortable: false },
      { text: "Description", value: "description", align: "start", sortable: false },
    ],
  }),
  computed: {
    ...mapGetters("account", ["hasAccess"]),
    ...mapState({
      account: (state) => state.account.data,
      totalItems: (state) => state.schedule.metaData.totalEntries,
      dataItems: (state) => state.schedule.data,
      schedLoading: (state) => state.schedule.loadingTable,
      taskStatus: (state) => state.task.status,
      task: (state) => state.task.single,
    }),

    computedFilter() {
      const { page, itemsPerPage } = this.tableOptions;
      const params = {
        PageNumber: page ? page : 1,
        PageSize: itemsPerPage > 0 ? itemsPerPage : 10,
        OrderBy: "startDate",
        Ascending: true
      };

      const startUtc = this.date
        ? this.localToUtc(this.momentLocal(this.date).startOf("day"))
        : this.localToUtc(this.momentLocal().startOf("day"));
      const start = startUtc.format('YYYY-MM-DD[T]HH:mm:ss');
      const end = startUtc.add(1,'day').format('YYYY-MM-DD[T]HH:mm:ss');

      params.startDate = start;
      params.endDate = end;

      if (!this.hasAccess('schedule.view')) {        
        params.employeeId = this.account.user.id;
      }

      return params;
    },

    // only set name column's length to as wide as it needs to be, up to a threshold
    nameLen() {
      const threshold = 40
      let len = 0
      this.formattedItems.forEach((item) => {
        if(item?.taskName?.length >= threshold) {
          len = threshold;
        } else if(item?.taskName?.length >= len) {
          len = item.taskName.length
        }
      })
      return { minWidth: len + 'ch'};
    }
  },
  watch: {
    tableOptions: {
      handler(n, o) {
        if (JSON.stringify(n) !== JSON.stringify(o)) {
          
          // table options refresh after the first dataitems update (which sets
          // loading to false), so this prevents a hiccup in the skeleton
          // loader on initial page load
          if(this.initialPageLoad) {
            this.initialPageLoad = false;
            this.refreshNoLoading();
          } else {
            this.refresh();
          }

        }
      },
      deep: true,
    },

    computedFilter: {
      handler(n, o) {
        if (JSON.stringify(n) !== JSON.stringify(o)) {
          this.loading = true;
          this.refresh();
        }
      },
      deep: true,
    },

    dataItems: {
      async handler(n) {
        const items = [];
        for(const i of n) {
          const displayTimes = this.getDisplayTimes(i);
          
          const item = {
            ...i,
            ...displayTimes,
          };
          items.push(item);
        }

        this.formattedItems = items.sort((a, b) => {
          // sort all day events to the top of the day
          if(a.isAllDay && !b.isAllDay) {
            return -1;
          } else if (b.isAllDay && !a.isAllDay) {
            return 1;
          } else {
            return 0;
          }
        });
        this.loading = false;
      },
      deep: true,
    },
  },

  async mounted() {
    this.date = this.momentLocal().startOf("day").format();

    await this.refresh();

    this.timer = setInterval(() => {
      this.refreshNoLoading();
    }, (120 * 1000));
  },

  beforeDestroy() {
    this.clearTableFilter();
    clearInterval(this.timer);
  },

  methods: {
    ...mapActions("schedule", ["clearTableFilter", "applyTableFilter"]),

    refresh: _.debounce(async function () {
      this.refreshTable(true);
    }, 500),

    async refreshTable(noLoading = true) {
      this.applyTableFilter({ ...this.computedFilter, noLoading });
    },

    async refreshNoLoading() {
      if (!this.schedLoading && !this.loading) {
        await this.refreshTable(false);
      }
    },

    colLength(content, threshold) {
      const width = content.length >= threshold ? threshold : content.length;
      return "min-width: " + width + "ch;"
    },

    today() {
      this.date = this.momentLocal().startOf("day").format();
    },

    prev() {
      this.date = this.momentLocal(this.date).subtract(1, "day").format();
    },

    next() {
      this.date = this.momentLocal(this.date).add(1, "day").format();
    },

    onEdit(item) {
      if (!this.hasAccess("schedule.edit")) {
        return false;
      }
      this.showForm = true;
      this.selectedItem = item;
    },

    onDelete(item) {
      this.showDelete = true;
      this.selectedItem = item;
    },

    onAdd() {
      this.showForm = true;
      this.selectedItem = {};
    },

    clearForm() {
      this.showForm = false;
      this.selectedItem = {};
    },

    clearDelete() {
      this.showDelete = false;
      this.selectedItem = {};
    },

    clearTask() {
      this.selectedItem = {};
    },

    getDisplayTimes(schedule) {
      const tableDate = this.date 
          ? this.momentLocal(this.date).format("YYYY-MM-DD")
          : this.momentLocal().format("YYYY-MM-DD");
        let localStart = this.momentLocal(schedule.startDate);
        let localEnd = this.momentLocal(schedule.endDate);

        // e.g., 10:00am – 11:00am
        let startDate = "";
        let endDate = "";
        let startTime = this.momentLocal(schedule.startDate).format("h:mma");
        let endTime = this.momentLocal(schedule.endDate).format("h:mma");

        // 10 – 11am
        startTime = startTime.replace(":00", "");
        endTime = endTime.replace(":00", "");

        // if showing Jan 1st and start time is Dec 31st...
        // 10am – 11am >> 12/31 10am – 1/1 11am
        // or showing Jan 1st and end time is Jan 2nd...
        // 10am – 11am >> 1/1 10am – 1/2 11am
        const scheduleStartDate = localStart.format("YYYY-MM-DD");
        const scheduleEndDate = localEnd.format("YYYY-MM-DD");
        let includingDates = false;
        if(tableDate !== scheduleStartDate || tableDate !== scheduleEndDate) {
          includingDates = true;
          startDate = localStart.format("M/D");
          endDate = localEnd.format("M/D");
        }

        // 10am – 11am >> 10 – 11am
        if(!includingDates && startTime.includes("am") && endTime.includes("am")) {
          startTime = startTime.replace("am", "");
        }
        
        // 10pm – 11pm >> 10 – 11pm
        if(!includingDates && startTime.includes("pm") && endTime.includes("pm")) {
          startTime = startTime.replace("pm", "")
        }

        const taskTime = schedule?.isAllday
          ? "All Day"
          : `${startDate} ${startTime} – ${endDate} ${endTime}`;
        
        return {
          fStartDate: startDate,
          fStartTime: startTime,
          fEndDate: endDate,
          fEndTime: endTime,
          taskTime: taskTime
        }
    },
  },
};
</script>
